import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Pagination, Modal, Form } from 'react-bootstrap';
// import { useSession } from '../App'; // Unused - commented out to fix ESLint warning
import MerchantNavigation from './MerchantNavigation';
import MeliCategoryLookup from './MeliCategoryLookup';
import CategoryPricing from './CategoryPricing';

const Products = () => {
    const { merchant } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // const { logout } = useSession(); // Unused - commented out to fix ESLint warning

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [showJsonModal, setShowJsonModal] = useState(false);
    const [selectedJson, setSelectedJson] = useState({ title: '', data: null });
    // const [modalView, setModalView] = useState('main'); // Unused - commented out to fix ESLint warning
    const [lastFetchTime, setLastFetchTime] = useState(null); // eslint-disable-line no-unused-vars
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [categoryData, setCategoryData] = useState(null);

    // New state for MELI category lookup
    const [showCategoryLookupModal, setShowCategoryLookupModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [categoryLookupResult, setCategoryLookupResult] = useState(null);
    const [categoryLookupLoading, setCategoryLookupLoading] = useState(false);
    const [categoryLookupError, setCategoryLookupError] = useState(null);

    // Add new state for filtering
    // Update the filterParams state to include marketplace_status
    const [filterParams, setFilterParams] = useState(() => {
        // Initialize filter params from URL search parameters
        const params = new URLSearchParams(window.location.search);
        return {
            name: params.get('name') || '',
            sku: params.get('sku') || '',
            price_operator: params.get('price_operator') || 'eq',
            price_value: params.get('price_value') || '',
            premium_listing: params.get('premium_listing') || 'all',
            marketplace_status: params.get('marketplace_status') || 'all'
        };
    });
    const [isFiltering, setIsFiltering] = useState(false);

    // Add new state for recategorization
    const [showRecategorizeModal, setShowRecategorizeModal] = useState(false);
    const [recategorizeInstructions, setRecategorizeInstructions] = useState('');
    const [desiredMeliCategoryId, setDesiredMeliCategoryId] = useState('');
    const [selectedProducts, setSelectedProducts] = useState(new Set());
    const [isRecategorizing, setIsRecategorizing] = useState(false);
    const [recategorizeResults, setRecategorizeResults] = useState(null);

    // Add new state for confirmation modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const ITEMS_PER_PAGE = 50;
    const API_BASE_URL = 'https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration';

    const categoryId = searchParams.get('category_id');
    const categoryName = searchParams.get('category_name');
    const marketplacesParam = searchParams.get('marketplaces'); // Add this line

    const [selectedCountry, setSelectedCountry] = useState(() => {
        // Initialize selected country from URL search parameters
        const params = new URLSearchParams(window.location.search);
        return params.get('country') || 'AR'; // Default to Argentina
    });



    // Update fetchProducts to handle pagination
    const fetchProducts = useCallback(async (forceRefresh = false, pageNum = currentPage, searchParams = null) => {
        if (!categoryId) {
            setError('No category ID provided');
            setLoading(false);
            return;
        }

        try {
            setIsRefreshing(true);
            setError(null);

            // Build URL with pagination parameters
            let url = `${API_BASE_URL}/merchant/${merchant}/products?type=products&category_id=${categoryId}&page=${pageNum}&page_size=${ITEMS_PER_PAGE}`;

            // Add country filter if selected
            if (selectedCountry && selectedCountry !== 'AR') {
                url += `&meli_fee_country=${selectedCountry}`;
            }

            // Add search parameters if provided
            if (searchParams) {
                if (searchParams.name) {
                    url += `&name=${encodeURIComponent(searchParams.name)}`;
                }
                if (searchParams.sku) {
                    url += `&sku=${encodeURIComponent(searchParams.sku)}`;
                }
                if (searchParams.price_value && searchParams.price_operator) {
                    const countryCode = selectedCountry.toLowerCase();
                    const operator = searchParams.price_operator;
                    // This is correct: price_{country}_{operator}
                    url += `&price_${countryCode}_${operator}=${searchParams.price_value}`;
                }
                // Add premium listing filter
                if (searchParams.premium_listing && searchParams.premium_listing !== 'all') {
                    url += `&premium_listing=${searchParams.premium_listing}`;
                }
                // Add marketplace status filter
                if (searchParams.marketplace_status && searchParams.marketplace_status !== 'all') {
                    const countryCode = selectedCountry.toLowerCase();
                    const marketplaceMapping = {
                        'ar': 'mla',
                        'br': 'mlb',
                        'mx': 'mlm',
                        'cl': 'mlc',
                        'co': 'mco'
                    };
                    const marketplace = marketplaceMapping[countryCode];
                    if (marketplace) {
                        url += `&${marketplace}_status=${searchParams.marketplace_status}`;
                    }
                }
            }

            console.log('Fetching products with URL:', url);

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const products = data.data || [];
            const categoryInfo = data.category_data || null;
            const paginationInfo = data.pagination || {};

            // Update pagination state
            setTotalProducts(paginationInfo.total_products || products.length);
            setTotalPages(paginationInfo.total_pages || 1);
            setCurrentPage(paginationInfo.current_page || pageNum);
            setCategoryData(categoryInfo);

            // Set products directly (no need for client-side pagination)
            setProducts(products);
            setLastFetchTime(Date.now());

        } catch (err) {
            console.error('Error fetching products:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [merchant, categoryId, selectedCountry, currentPage, ITEMS_PER_PAGE]);

    // Force refresh function
    const handleForceRefresh = () => {
        // Check if there's an active search
        const urlParams = new URLSearchParams(window.location.search);
        const hasSearchFlag = urlParams.get('searched') === 'true';

        if (hasSearchFlag) {
            // Apply filters when refreshing if there's an active search
            fetchProducts(true, currentPage, filterParams);
        } else {
            // Refresh without filters
            fetchProducts(true);
        }
    };

    // Function to update URL with current filter parameters (without search flag)
    const updateURLWithFilters = useCallback((newFilterParams) => {
        const url = new URL(window.location);

        // Update or remove filter parameters
        if (newFilterParams.name) {
            url.searchParams.set('name', newFilterParams.name);
        } else {
            url.searchParams.delete('name');
        }

        if (newFilterParams.sku) {
            url.searchParams.set('sku', newFilterParams.sku);
        } else {
            url.searchParams.delete('sku');
        }

        if (newFilterParams.price_operator && newFilterParams.price_operator !== 'eq') {
            url.searchParams.set('price_operator', newFilterParams.price_operator);
        } else {
            url.searchParams.delete('price_operator');
        }

        if (newFilterParams.price_value) {
            url.searchParams.set('price_value', newFilterParams.price_value);
        } else {
            url.searchParams.delete('price_value');
        }

        if (newFilterParams.premium_listing && newFilterParams.premium_listing !== 'all') {
            url.searchParams.set('premium_listing', newFilterParams.premium_listing);
        } else {
            url.searchParams.delete('premium_listing');
        }

        if (newFilterParams.marketplace_status && newFilterParams.marketplace_status !== 'all') {
            url.searchParams.set('marketplace_status', newFilterParams.marketplace_status);
        } else {
            url.searchParams.delete('marketplace_status');
        }

        // Note: We don't add the 'searched' flag here - that's only added when user clicks SEARCH
        // Update the URL without reloading the page
        window.history.replaceState({}, '', url);
    }, []);



    // Handle filter search
    const handleFilterSearch = async () => {
        setIsFiltering(true);
        setCurrentPage(1); // Reset to first page when filtering

        // Update URL with filter parameters and add search flag
        updateURLWithFilters(filterParams);
        const url = new URL(window.location);
        url.searchParams.set('searched', 'true');
        window.history.replaceState({}, '', url);

        try {
            await fetchProducts(false, 1, filterParams);
        } finally {
            setIsFiltering(false);
        }
    };

    // Clear filters
    const handleClearFilters = () => {
        const clearedFilters = {
            name: '',
            sku: '',
            price_operator: 'eq', // Fix: Use 'eq' instead of '='
            price_value: '',
            premium_listing: 'all', // Reset to 'all'
            marketplace_status: 'all' // Reset to 'all'
        };
        setFilterParams(clearedFilters);
        setCurrentPage(1);

        // Clear URL parameters and remove search flag
        updateURLWithFilters(clearedFilters);
        const url = new URL(window.location);
        url.searchParams.delete('searched');
        window.history.replaceState({}, '', url);

        fetchProducts(false, 1, null); // Fetch without filters
    };

    // Initial load and cache check
    useEffect(() => {
        // Check if there's a search flag in the URL indicating filters were explicitly applied
        const urlParams = new URLSearchParams(window.location.search);
        const hasSearchFlag = urlParams.get('searched') === 'true';

        if (hasSearchFlag) {
            // Apply filters on initial load only if they were explicitly searched
            fetchProducts(false, currentPage, filterParams);
        } else {
            fetchProducts();
        }
    }, [merchant, categoryId, currentPage, fetchProducts]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update handlePageChange to fetch new page from API
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0);

        // Check if there's an active search
        const urlParams = new URLSearchParams(window.location.search);
        const hasSearchFlag = urlParams.get('searched') === 'true';

        if (hasSearchFlag) {
            // Apply filters when changing pages if there's an active search
            fetchProducts(false, page, filterParams);
        } else {
            // Fetch new page without filters
            fetchProducts(false, page);
        }
    };

    const handleBackToCategories = () => {
        // Build URL with marketplace filters if they exist
        let url = `/private/${merchant}/categories`;

        // Add marketplace filters to URL if they exist
        if (marketplacesParam) {
            url += `?marketplaces=${marketplacesParam}`;
        }

        navigate(url);
    };

    const handleJsonClick = (title, data) => {
        setSelectedJson({ title, data });
        setShowJsonModal(true);
    };

    // Handle MELI category lookup
    const handleCategoryLookup = async (product) => {
        setSelectedProduct(product);
        setCategoryLookupResult(null);
        setCategoryLookupError(null);
        setCategoryLookupLoading(true);
        setShowCategoryLookupModal(true);

        try {
            const response = await fetch(
                `${API_BASE_URL}/merchant/${merchant}/products/category_predict`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'category_lookup',
                        product_id: product.id
                    })
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setCategoryLookupResult(data);
            setCategoryLookupLoading(false);
        } catch (err) {
            console.error('Error in category lookup:', err);
            setCategoryLookupError(err.message);
            setCategoryLookupLoading(false);
        }
    };

    // Update handleCountrySelect to reset to page 1 but preserve filters
    const handleCountrySelect = async (countryCode) => {
        setSelectedCountry(countryCode);
        setCurrentPage(1); // Reset to first page when changing country

        // Update URL with selected country and remove search flag
        const url = new URL(window.location);
        url.searchParams.set('country', countryCode);
        url.searchParams.delete('searched'); // Remove search flag when changing country
        window.history.replaceState({}, '', url);

        // Reload products with country filter (without applying current filters)
        try {
            setIsRefreshing(true);
            setError(null);

            let url = `${API_BASE_URL}/merchant/${merchant}/products?type=products&category_id=${categoryId}&page=1&page_size=${ITEMS_PER_PAGE}`;
            if (countryCode) {
                url += `&meli_fee_country=${countryCode}`;
            }

            // Don't apply current filter parameters when changing country
            // User needs to click SEARCH again to apply filters to the new country

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const products = data.data || [];
            const categoryInfo = data.category_data || null;
            const paginationInfo = data.pagination || {};

            setTotalProducts(paginationInfo.total_products || products.length);
            setTotalPages(paginationInfo.total_pages || 1);
            setCurrentPage(1);
            setCategoryData(categoryInfo);
            setProducts(products);
            setLastFetchTime(Date.now());

        } catch (err) {
            console.error('Error fetching products with country filter:', err);
            setError(err.message);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Helper function to get MELI price for selected country
    const getMeliPriceForCountry = (product, countryCode) => {
        const meliPriceField = `meli_price_${countryCode.toLowerCase()}`;
        const meliPrice = product[meliPriceField];
        return meliPrice ? parseFloat(meliPrice).toFixed(2) : null;
    };

    // Helper function to get shipping fee for selected country
    const getShippingFeeForCountry = (product, countryCode) => {
        const shippingFeeField = `shipping_fee_${countryCode.toLowerCase()}`;
        const shippingFee = product[shippingFeeField];
        return shippingFee ? parseFloat(shippingFee).toFixed(2) : null;
    };

    // Helper function to check if product has premium listing for selected country
    const hasPremiumListing = (product, countryCode) => {
        const premiumListingField = `premium_listing_${countryCode.toLowerCase()}`;
        return product[premiumListingField] === 1;
    };
    const country_marketplace_mapping = {  // eslint-disable-line no-unused-vars
        'ar': 'mla',
        'br': 'mlb',
        'mx': 'mlm',
        'cl': 'mlc',
        'co': 'mco'
    };
    // Helper function to get marketplace status for a specific country
    const getMarketplaceStatusForCountry = (product, countryCode) => {
        // Check if product has cbt_id (required for publishing)
        //console.log('product', product);

        if (product.status === 'paused') {
            return 'PAUSED';
        }

        if (!product.cbt_id && product.publish_error) {
            return 'ERROR PUBLISHING';
        }

        if (!product.cbt_id) {
            return 'NOT PUBLISHED';
        }

        const country_marketplace_mapping = {
            'ar': 'mla',
            'br': 'mlb',
            'mx': 'mlm',
            'cl': 'mlc',
            'co': 'mco'
        };

        const marketplaceField = country_marketplace_mapping[countryCode.toLowerCase()];

        // Check if the marketplace field exists and has a value
        if (!marketplaceField || !product[marketplaceField]) {

            return 'NOT PUBLISHED';
        }

        // Return the actual status if both conditions are met
        const statusField = `${marketplaceField}_status`;
        return product[statusField] || 'active';
    };

    // Map country code to marketplace key
    const getMarketplaceKey = (countryCode) => {
        const map = { AR: 'mla', BR: 'mlb', MX: 'mlm', CL: 'mlc', CO: 'mco' };
        return map[(countryCode || '').toUpperCase()] || null;
    };

    const getPermalinkForSelectedCountry = (product) => {
        if (!product?.meli_permalinks) return null;

        const key = getMarketplaceKey(selectedCountry);
        if (!key) return null;

        let links = product.meli_permalinks;
        if (typeof links === 'string') {
            try { links = JSON.parse(links); } catch { return null; }
        }
        return links?.[key] || null;
    };

    // Update the getStatusBadgeColor function
    const getStatusBadgeColor = (status) => {
        if (!status) return 'success'; // Change from 'secondary' to 'success' for no status

        const statusLower = status.toLowerCase();
        if (statusLower.includes('error publishing')) return 'danger';
        if (statusLower.includes('active')) return 'success';
        if (statusLower.includes('under_review') || statusLower.includes('pending')) return 'danger';
        if (statusLower.includes('paused') || statusLower.includes('suspended')) return 'warning';
        if (statusLower.includes('forbidden') || statusLower.includes('blocked')) return 'dark';
        if (statusLower.includes('draft')) return 'info';

        return 'secondary';
    };




    // Update page title when category changes
    useEffect(() => {
        if (categoryName && categoryId) {
            document.title = `${merchant} - ${categoryName} (${categoryId}) - Products`;
        } else {
            document.title = `${merchant} - Products`;
        }
    }, [categoryName, categoryId, merchant]);

    // Update URL when page changes
    useEffect(() => {
        if (categoryId) {
            const url = new URL(window.location);
            url.searchParams.set('page', currentPage.toString());
            window.history.replaceState({}, '', url);
        }
    }, [currentPage, categoryId]);



    // Read page from URL on component mount
    useEffect(() => {
        if (categoryId) {
            const urlParams = new URLSearchParams(window.location.search);
            const pageParam = urlParams.get('page');
            if (pageParam) {
                const pageNum = parseInt(pageParam);
                if (pageNum !== currentPage) {
                    setCurrentPage(pageNum);
                    fetchProducts(false, pageNum);
                }
            }
        }
    }, [categoryId, currentPage, fetchProducts]);

    // Function to render active filters
    const renderActiveFilters = () => {
        const activeFilters = [];

        if (filterParams.name) {
            activeFilters.push(`Name like "${filterParams.name}"`);
        }

        if (filterParams.sku) {
            activeFilters.push(`SKU = "${filterParams.sku}"`);
        }

        if (filterParams.price_value && filterParams.price_operator) {
            const operatorSymbol = filterParams.price_operator === 'eq' ? '=' :
                filterParams.price_operator === 'gt' ? '>' : '<';
            activeFilters.push(`Price ${selectedCountry} ${operatorSymbol} ${filterParams.price_value}`);
        }

        // Add premium listing filter to active filters display
        if (filterParams.premium_listing && filterParams.premium_listing !== 'all') {
            const premiumText = filterParams.premium_listing === 'yes' ? 'Premium Only' : 'Classic Only';
            activeFilters.push(`Premium Listing: ${premiumText}`);
        }

        // Add marketplace status filter to active filters display
        if (filterParams.marketplace_status && filterParams.marketplace_status !== 'all') {
            const statusText = filterParams.marketplace_status === 'active' ? 'Active Only' : 'Paused Only';
            activeFilters.push(`Status ${selectedCountry}: ${statusText}`);
        }

        if (activeFilters.length === 0) {
            return null;
        }

        return (
            <div className="mb-3 p-3 bg-light border rounded">
                <div className="d-flex align-items-center gap-2 mb-2">
                    <strong className="text-muted">🔍 Active Filters:</strong>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleClearFilters}
                        disabled={isFiltering}
                    >
                        🗑️ Clear All
                    </Button>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    {activeFilters.map((filter, index) => (
                        <Badge key={index} bg="info" className="fs-6 px-3 py-2">
                            {filter}
                        </Badge>
                    ))}
                </div>
            </div>
        );
    };

    // Handle select all products
    const handleSelectAll = () => {
        if (selectedProducts.size === products.length) {
            // If all are selected, deselect all
            setSelectedProducts(new Set());
        } else {
            // Select all products
            setSelectedProducts(new Set(products.map(p => p.id)));
        }
    };

    // Handle individual product selection
    const handleProductSelect = (productId) => {
        const newSelected = new Set(selectedProducts);
        if (newSelected.has(productId)) {
            newSelected.delete(productId);
        } else {
            newSelected.delete(productId);
            newSelected.add(productId);
        }
        setSelectedProducts(newSelected);
    };

    // Handle recategorization
    const handleRecategorize = async () => {
        if (selectedProducts.size === 0) {
            alert('Please select at least one product to recategorize.');
            return;
        }

        if (!recategorizeInstructions.trim()) {
            alert('Please enter instructions for recategorization.');
            return;
        }

        if (!desiredMeliCategoryId.trim()) {
            alert('Please enter the desired MELI Category ID.');
            return;
        }

        // Show confirmation modal instead of alert
        setShowConfirmModal(true);
    };

    // Handle confirmed recategorization
    const handleConfirmedRecategorize = async () => {
        setShowConfirmModal(false);
        setIsRecategorizing(true);
        setRecategorizeResults(null);

        try {
            const response = await fetch('https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/category-reassign', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    product_ids: Array.from(selectedProducts),
                    desired_meli_category_id: desiredMeliCategoryId,
                    desired_description_instructions: recategorizeInstructions
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setRecategorizeResults(data);

            // Clear selections after successful recategorization
            setSelectedProducts(new Set());
            setRecategorizeInstructions('');
            setDesiredMeliCategoryId('');

            // Refresh products to show updated categories
            fetchProducts(true);

        } catch (err) {
            console.error('Error recategorizing products:', err);
            setRecategorizeResults({
                error: `Failed to recategorize products: ${err.message}`
            });
        } finally {
            setIsRecategorizing(false);
        }
    };

    // Toggle recategorization mode
    const toggleRecategorizeMode = () => {
        if (showRecategorizeModal) {
            // If already in recategorization mode, disable it and clear selections
            setShowRecategorizeModal(false);
            setSelectedProducts(new Set());
            setRecategorizeInstructions('');
            setDesiredMeliCategoryId('');
            setRecategorizeResults(null);
        } else {
            // Enable recategorization mode
            setShowRecategorizeModal(true);
        }
    };

    return (
        <>
            <MerchantNavigation currentPage="products" />

            <Container className="py-5">
                {/* Breadcrumb Navigation */}
                {categoryName && categoryId && (
                    <Row className="mb-3">
                        <Col>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb justify-content-center">
                                    <li className="breadcrumb-item">
                                        <Button
                                            variant="link"
                                            className="p-0 text-decoration-none"
                                            onClick={handleBackToCategories}
                                        >
                                            📁 Categories
                                        </Button>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        <span className="text-primary">
                                            📂 {categoryName}
                                        </span>
                                    </li>
                                </ol>
                            </nav>
                        </Col>
                    </Row>
                )}

                {/* Category Information and Pricing */}
                {categoryName && categoryId && (
                    <Row className="mb-4">
                        <Col md={6}>
                            <Card className="shadow-sm border-primary">
                                <Card.Body className="p-3">
                                    <div>
                                        <h6 className="text-primary mb-2">
                                            📂 Category Information
                                        </h6>
                                        <div className="row">
                                            <h3>
                                                {categoryName}
                                            </h3>
                                            <h4>
                                                {categoryId}
                                            </h4>
                                        </div>

                                        {/* Marketplace Publishing Status */}
                                        {categoryData && (
                                            <div className="mt-3">
                                                <div className="d-flex flex-wrap gap-2">
                                                    {categoryData.publish_to_mla && (
                                                        <Button
                                                            variant={selectedCountry === 'AR' ? "primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="fs-6 px-3 py-2"
                                                            onClick={() => handleCountrySelect('AR')}
                                                        >
                                                            🇦🇷 Argentina
                                                        </Button>
                                                    )}
                                                    {categoryData.publish_to_mlc && (
                                                        <Button
                                                            variant={selectedCountry === 'CL' ? "primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="fs-6 px-3 py-2"
                                                            onClick={() => handleCountrySelect('CL')}
                                                        >
                                                            🇨🇱 Chile
                                                        </Button>
                                                    )}
                                                    {categoryData.publish_to_mlb && (
                                                        <Button
                                                            variant={selectedCountry === 'BR' ? "primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="fs-6 px-3 py-2"
                                                            onClick={() => handleCountrySelect('BR')}
                                                        >
                                                            🇧🇷 Brasil
                                                        </Button>
                                                    )}
                                                    {categoryData.publish_to_mlm && (
                                                        <Button
                                                            variant={selectedCountry === 'MX' ? "primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="fs-6 px-3 py-2"
                                                            onClick={() => handleCountrySelect('MX')}
                                                        >
                                                            🇲🇽 Mexico
                                                        </Button>
                                                    )}
                                                    {categoryData.publish_to_mco && (
                                                        <Button
                                                            variant={selectedCountry === 'CO' ? "primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="fs-6 px-3 py-2"
                                                            onClick={() => handleCountrySelect('CO')}
                                                        >
                                                            🇨🇴 Colombia
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <CategoryPricing
                                categoryName={categoryName}
                                categoryId={categoryId}
                                selectedCountry={selectedCountry}
                                onSave={(pricingData) => {
                                    console.log('Pricing formula saved:', pricingData);
                                    // TODO: Implement API call to save pricing and update products
                                }}
                                onFeeUpdated={(feeData) => {
                                    console.log('Fee updated, refreshing products:', feeData);

                                    // Clear premium listing filters if requested
                                    if (feeData.clearPremiumFilters) {
                                        setFilterParams(prev => ({
                                            ...prev,
                                            premium_listing: 'all'
                                        }));
                                    }

                                    // Refresh the products list to show updated MELI prices
                                    fetchProducts(true);
                                }}
                            />
                        </Col>
                    </Row>
                )}




                <Row>
                    <Col>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handleBackToCategories}
                                        >
                                            ← Back to Categories
                                        </Button>
                                        <Button
                                            variant={showRecategorizeModal ? "outline-warning" : "warning"}
                                            size="sm"
                                            onClick={toggleRecategorizeMode}
                                            disabled={products.length === 0}
                                        >
                                            {showRecategorizeModal ? (
                                                '❌ Cancel Recategorization'
                                            ) : (
                                                '🔄 Recategorize Products'
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleForceRefresh}
                                            disabled={isRefreshing}
                                        >
                                            {isRefreshing ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Refreshing...
                                                </>
                                            ) : (
                                                <>
                                                    🔄 Refresh
                                                </>
                                            )}
                                        </Button>

                                        <span className="text-muted">{totalProducts} products</span>
                                    </div>
                                </div>

                                {/* Search Fields */}
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label className="small text-muted">Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Product"
                                                value={filterParams.name}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    setFilterParams(prev => ({ ...prev, name: newValue }));
                                                }}
                                                disabled={isFiltering}
                                                size="sm"
                                                style={{ width: '200px' }}
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Group>
                                            <Form.Label className="small text-muted">SKU</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="SKU"
                                                value={filterParams.sku}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    setFilterParams(prev => ({ ...prev, sku: newValue }));
                                                }}
                                                disabled={isFiltering}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label className="small text-muted">Price ({selectedCountry})</Form.Label>
                                            <div className="d-flex gap-1">
                                                <Form.Select
                                                    value={filterParams.price_operator}
                                                    onChange={(e) => {
                                                        const newValue = e.target.value;
                                                        setFilterParams(prev => ({ ...prev, price_operator: newValue }));
                                                    }}
                                                    disabled={isFiltering}
                                                    size="sm"
                                                    style={{ width: '60px' }}
                                                >
                                                    <option value="eq">=</option>
                                                    <option value="gt">&gt;</option>
                                                    <option value="lt">&lt;</option>
                                                </Form.Select>
                                                <Form.Control
                                                    type="number"
                                                    value={filterParams.price_value}
                                                    onChange={(e) => {
                                                        const newValue = e.target.value;
                                                        setFilterParams(prev => ({ ...prev, price_value: newValue }));
                                                    }}
                                                    disabled={isFiltering}
                                                    min="0"
                                                    step="1"
                                                    size="sm"
                                                    placeholder="0"
                                                    style={{ width: '100px' }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Group>
                                            <Form.Label className="small text-muted">💎 Premium</Form.Label>
                                            <Form.Select
                                                value={filterParams.premium_listing}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    setFilterParams(prev => ({ ...prev, premium_listing: newValue }));
                                                }}
                                                disabled={isFiltering}
                                                size="sm"
                                            >
                                                <option value="all">All Products</option>
                                                <option value="yes">Premium Only</option>
                                                <option value="no">Classic Only</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Group>
                                            <Form.Label className="small text-muted">🏪 Status ({selectedCountry})</Form.Label>
                                            <Form.Select
                                                value={filterParams.marketplace_status}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    setFilterParams(prev => ({ ...prev, marketplace_status: newValue }));
                                                }}
                                                disabled={isFiltering}
                                                size="sm"
                                            >
                                                <option value="all">All Products</option>
                                                <option value="active">Active Only</option>
                                                <option value="paused">Paused Only</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-12 d-flex gap-2 mt-2">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={handleFilterSearch}
                                            disabled={isFiltering}
                                        >
                                            {isFiltering ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Searching...
                                                </>
                                            ) : (
                                                '🔍 Search'
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handleClearFilters}
                                            disabled={isFiltering}
                                        >
                                            🗑️ Clear
                                        </Button>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {!categoryId && (
                                    <Alert variant="warning">
                                        <strong>No category selected</strong>
                                        <br />
                                        <small>Please select a category from the categories page to view products.</small>
                                        <div className="mt-2">
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                onClick={handleBackToCategories}
                                            >
                                                ← Go to Categories
                                            </Button>
                                        </div>
                                    </Alert>
                                )}

                                {loading && !isRefreshing && (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2 text-muted">Loading products...</p>
                                    </div>
                                )}

                                {error && (
                                    <Alert variant="danger">
                                        <strong>Error loading products:</strong> {error}
                                        <div className="mt-2">
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={handleForceRefresh}
                                            >
                                                Try Again
                                            </Button>
                                        </div>
                                    </Alert>
                                )}

                                {!loading && !error && products.length === 0 && categoryId && (
                                    <Alert variant="info">
                                        <strong>No products found</strong>
                                        <br />
                                        <small>This category doesn't have any products yet.</small>
                                    </Alert>
                                )}

                                {/* Show spinner when refreshing or loading */}
                                {(loading || isRefreshing) && (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" size="lg" />
                                        <p className="mt-3 text-muted">
                                            {isRefreshing ? 'Refreshing products...' : 'Loading products...'}
                                        </p>
                                    </div>
                                )}

                                {/* Show products list only when not loading/refreshing */}
                                {!loading && !isRefreshing && !error && products.length > 0 && (
                                    <>
                                        <div className="mb-3">
                                            <small className="text-muted">
                                                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalProducts)} of {totalProducts} products
                                            </small>
                                        </div>

                                        {/* Display Active Filters */}
                                        {renderActiveFilters()}

                                        {/* Recategorization Controls */}
                                        {showRecategorizeModal && (
                                            <div className="mb-4 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                                                <div className="row align-items-end">
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-bold">Instructions:</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="e.g., These products are wristwatches"
                                                            value={recategorizeInstructions}
                                                            onChange={(e) => setRecategorizeInstructions(e.target.value)}
                                                            disabled={isRecategorizing}
                                                        />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label fw-bold">Desired MELI Category ID:</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="e.g., CBT1442"
                                                            value={desiredMeliCategoryId}
                                                            onChange={(e) => setDesiredMeliCategoryId(e.target.value)}
                                                            disabled={isRecategorizing}
                                                        />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label fw-bold">Selected Products:</label>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={handleSelectAll}
                                                                disabled={isRecategorizing}
                                                            >
                                                                {selectedProducts.size === products.length ? 'Deselect All' : 'Select All'}
                                                            </Button>
                                                            <span className="badge bg-primary fs-6 px-3 py-2">
                                                                {selectedProducts.size} selected
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            onClick={handleRecategorize}
                                                            disabled={isRecategorizing || selectedProducts.size === 0 || !recategorizeInstructions.trim() || !desiredMeliCategoryId.trim()}
                                                        >
                                                            {isRecategorizing ? (
                                                                <>
                                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                                    Processing...
                                                                </>
                                                            ) : (
                                                                'Continue'
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Top Pagination */}
                                        {totalPages > 1 && (
                                            <div className="d-flex justify-content-center mb-4">
                                                <Pagination>
                                                    <Pagination.First
                                                        onClick={() => handlePageChange(1)}
                                                        disabled={currentPage === 1}
                                                    />
                                                    <Pagination.Prev
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    />

                                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                        let pageNum;
                                                        if (totalPages <= 5) {
                                                            pageNum = i + 1;
                                                        } else if (currentPage <= 3) {
                                                            pageNum = i + 1;
                                                        } else if (currentPage >= totalPages - 2) {
                                                            pageNum = totalPages - 4 + i;
                                                        } else {
                                                            pageNum = currentPage - 2 + i;
                                                        }

                                                        return (
                                                            <Pagination.Item
                                                                key={pageNum}
                                                                active={pageNum === currentPage}
                                                                onClick={() => handlePageChange(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </Pagination.Item>
                                                        );
                                                    })}

                                                    <Pagination.Next
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                    />
                                                    <Pagination.Last
                                                        onClick={() => handlePageChange(totalPages)}
                                                        disabled={currentPage === totalPages}
                                                    />
                                                </Pagination>
                                            </div>
                                        )}

                                        {/* Products List */}
                                        <Row>
                                            {products.map((product, index) => (
                                                <Col key={product.id || index} xs={12} className="mb-3">
                                                    <Card className="product-card">
                                                        <Card.Body>
                                                            {/* Selection Checkbox - Always show when in recategorization mode */}
                                                            {showRecategorizeModal && (
                                                                <div className="mb-2">
                                                                    <Badge bg="warning" className="me-3 align-middle justify-content-center text-size-md" text="dark" style={{ padding: '10px 20px', fontSize: '1.0rem' }}>
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            id={`select-${product.id}`}
                                                                            checked={selectedProducts.has(product.id)}
                                                                            onChange={() => handleProductSelect(product.id)}
                                                                            disabled={isRecategorizing}
                                                                            label={`Select product ${product.sku || product.id} for recategorization`}

                                                                        />
                                                                    </Badge>
                                                                </div>
                                                            )}

                                                            {/* Images - Thumbnails above title */}
                                                            {product.images && product.images.length > 0 && (
                                                                <div className="mb-3">
                                                                    <div className="d-flex gap-2 flex-wrap">
                                                                        {product.images.map((imageUrl, imgIndex) => (
                                                                            <img
                                                                                key={imgIndex}
                                                                                src={imageUrl}
                                                                                alt={`${imgIndex + 1}`}
                                                                                className="product-thumbnail"
                                                                                style={{
                                                                                    maxWidth: '100px',
                                                                                    maxHeight: '100px',
                                                                                    objectFit: 'cover',
                                                                                    cursor: 'pointer',
                                                                                    border: '2px solid #e9ecef',
                                                                                    borderRadius: '8px'
                                                                                }}
                                                                                onClick={() => handleJsonClick(
                                                                                    `Product Image - ${product.sku || product.id}`,
                                                                                    { imageUrl, productTitle: product.title }
                                                                                )}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Title - One long line */}
                                                            <h5 className="text-primary mb-2" title={product.title}>
                                                                {product.title || 'No Title'}
                                                            </h5>
                                                            {(() => {
                                                                const url = getPermalinkForSelectedCountry(product);
                                                                return url ? (
                                                                    <div className="m-2">
                                                                        <a href={url} target="_blank" rel="noopener noreferrer">
                                                                            {url} ↗
                                                                        </a>
                                                                    </div>
                                                                ) : null;
                                                            })()}
                                                            {/* Description - Text block */}
                                                            {product.description && (
                                                                <p className="text-muted mb-3">
                                                                    {product.description}
                                                                </p>
                                                            )}

                                                            {/* Product details in a row */}
                                                            <div className="d-flex flex-wrap gap-3 mb-3">
                                                                <div>
                                                                    <strong>SKU</strong>
                                                                    <Badge
                                                                        bg="secondary"
                                                                        className="ms-2"
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            if (product.sku) {
                                                                                navigator.clipboard.writeText(product.sku);
                                                                                //alert(`SKU ${product.sku} copied to clipboard!`);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {product.sku || 'N/A'}
                                                                    </Badge>
                                                                </div>
                                                                <div>
                                                                    {product.brand ? (
                                                                        <Badge bg="info">
                                                                            {product.brand}
                                                                        </Badge>
                                                                    ) : (
                                                                        <span className="text-muted ms-2">-</span>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <strong>Stock</strong>
                                                                    <Badge bg={product.stock > 0 ? 'success' : 'danger'} className="ms-2">
                                                                        {product.stock || 0}
                                                                    </Badge>
                                                                </div>
                                                            </div>

                                                            {/* Pricing and Weight Section - Right Side */}
                                                            <div className="d-flex justify-content-end gap-4 mb-3">
                                                                <div>

                                                                    {(() => {
                                                                        const status = getMarketplaceStatusForCountry(product, selectedCountry);
                                                                        const badgeColor = getStatusBadgeColor(status);
                                                                        const displayStatus = status || 'active'; // Change from 'No Status' to 'active'

                                                                        return (
                                                                            <Badge bg={badgeColor} className="ms-2 fs-4">
                                                                                {displayStatus}
                                                                            </Badge>
                                                                        );
                                                                    })()}
                                                                </div>
                                                                <div className="text-center">
                                                                    <small className="text-muted d-block">Weight</small>
                                                                    <span className="text-primary fw-bold fs-6">
                                                                        {product.shipping_weight_lb ? `${parseFloat(product.shipping_weight_lb).toFixed(2)} lb` : '--'}
                                                                    </span>
                                                                </div>
                                                                <div className="text-center">
                                                                    <small className="text-muted d-block">Shipping {selectedCountry}</small>
                                                                    <span className="text-info fw-bold fs-6">
                                                                        {getShippingFeeForCountry(product, selectedCountry)
                                                                            ? `$${getShippingFeeForCountry(product, selectedCountry)}`
                                                                            : '--'
                                                                        }
                                                                    </span>
                                                                </div>
                                                                <div className="text-center">
                                                                    <small className="text-muted d-block">MSRP</small>
                                                                    <span className="text-success fw-bold fs-6">
                                                                        ${product.price ? parseFloat(product.price).toFixed(2) : '0.00'}
                                                                    </span>
                                                                </div>


                                                                <div className="text-center">
                                                                    <small className="text-muted d-block">
                                                                        MELI Price {selectedCountry}
                                                                    </small>
                                                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                                                        <span className="text-danger fw-bold fs-6">
                                                                            {getMeliPriceForCountry(product, selectedCountry)
                                                                                ? `$${getMeliPriceForCountry(product, selectedCountry)}`
                                                                                : '--'
                                                                            }
                                                                        </span>
                                                                        {hasPremiumListing(product, selectedCountry) && (
                                                                            <span
                                                                                title="Premium listing - higher pricing applied"
                                                                                className="text-warning"
                                                                                style={{ fontSize: '1.2em' }}
                                                                            >
                                                                                💎
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>


                                                            {/* Action Buttons */}
                                                            <div className="d-flex gap-2">
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() => handleJsonClick(
                                                                        `Product Details - ${product.sku || product.id}`,
                                                                        {
                                                                            meli_attributes: product.meli_attributes,
                                                                            attributes: product.attributes,
                                                                            full_product: product
                                                                        }
                                                                    )}
                                                                >
                                                                    View Details
                                                                </Button>

                                                                <Button
                                                                    variant="outline-warning"
                                                                    size="sm"
                                                                    onClick={() => handleJsonClick(
                                                                        `MELI Attributes - ${product.sku || product.id}`,
                                                                        product.meli_attributes || {}
                                                                    )}

                                                                    title="View MELI attributes for this product"
                                                                >
                                                                    🏷️ View MELI Attributes
                                                                </Button>

                                                                <Button
                                                                    variant="outline-info"
                                                                    size="sm"
                                                                    onClick={() => handleCategoryLookup(product)}
                                                                    disabled={categoryLookupLoading}
                                                                    title="Look up the suggested MELI category for this product"
                                                                >
                                                                    {categoryLookupLoading ? (
                                                                        <>
                                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                                            Looking up...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            🔍 Lookup MELI Category
                                                                        </>
                                                                    )}
                                                                </Button>
                                                                {
                                                                    !!product.publish_error && (
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => handleJsonClick(
                                                                                `Publish Error - ${product.sku || product.id}`,
                                                                                JSON.parse(product.publish_error) || {}
                                                                            )}
                                                                        >
                                                                            🔍 View Publish Error
                                                                        </Button>
                                                                    )}

                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Bottom Pagination */}
                                        {totalPages > 1 && (
                                            <div className="d-flex justify-content-center mt-4">
                                                <Pagination>
                                                    <Pagination.First
                                                        onClick={() => handlePageChange(1)}
                                                        disabled={currentPage === 1}
                                                    />
                                                    <Pagination.Prev
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    />

                                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                        let pageNum;
                                                        if (totalPages <= 3) {
                                                            pageNum = i + 1;
                                                        } else if (currentPage <= 3) {
                                                            pageNum = i + 1;
                                                        } else if (currentPage >= totalPages - 2) {
                                                            pageNum = totalPages - 4 + i;
                                                        } else {
                                                            pageNum = currentPage - 2 + i;
                                                        }

                                                        return (
                                                            <Pagination.Item
                                                                key={pageNum}
                                                                active={pageNum === currentPage}
                                                                onClick={() => handlePageChange(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </Pagination.Item>
                                                        );
                                                    })}

                                                    <Pagination.Next
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                    />
                                                    <Pagination.Last
                                                        onClick={() => handlePageChange(totalPages)}
                                                        disabled={currentPage === totalPages}
                                                    />
                                                </Pagination>
                                            </div>
                                        )}
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* JSON Modal */}
            <Modal
                show={showJsonModal}
                onHide={() => setShowJsonModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedJson.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Check if this is an image modal */}
                    {selectedJson.data && selectedJson.data.imageUrl ? (
                        <div className="text-center">
                            <img
                                src={selectedJson.data.imageUrl}
                                alt={selectedJson.data.productTitle || 'Product image'}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain'
                                }}
                            />
                            {selectedJson.data.productTitle && (
                                <p className="mt-3 text-muted">
                                    {selectedJson.data.productTitle}
                                </p>
                            )}
                        </div>
                    ) : (
                        <pre className="json-display">
                            {JSON.stringify(selectedJson.data, null, 2)}
                        </pre>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowJsonModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MELI Category Lookup Modal */}
            <MeliCategoryLookup
                show={showCategoryLookupModal}
                onHide={() => {
                    setShowCategoryLookupModal(false);
                    setSelectedProduct(null);
                    setCategoryLookupResult(null);
                    setCategoryLookupError(null);
                }}
                selectedProduct={selectedProduct}
                categoryLookupResult={categoryLookupResult}
                categoryLookupLoading={categoryLookupLoading}
                categoryLookupError={categoryLookupError}
                categoryName={categoryName}
                onViewAttributes={(suggestion) => {
                    setSelectedJson({
                        title: `Attributes for ${suggestion.category_name}`,
                        data: suggestion.attributes
                    });
                    setShowJsonModal(true);
                    setShowCategoryLookupModal(false);
                }}
                onViewFullResponse={() => {
                    setSelectedJson({
                        title: 'Full MELI API Response',
                        data: categoryLookupResult.meli_full_response
                    });
                    setShowJsonModal(true);
                }}
            />



            {/* Confirmation Modal */}
            <Modal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                size="md"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>🔄 Confirm Recategorization</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <div className="mb-3">
                            <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h5>Are you sure you want to proceed?</h5>
                        <p className="text-muted">
                            I will attempt to recategorize <strong>{selectedProducts.size}</strong> selected products.
                        </p>
                        <div className="bg-light p-3 rounded">
                            <small>
                                <strong>Instructions</strong> {recategorizeInstructions}<br />
                                <strong>Category ID:</strong> {desiredMeliCategoryId}
                            </small>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowConfirmModal(false)}
                        disabled={isRecategorizing}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleConfirmedRecategorize}
                        disabled={isRecategorizing}
                    >
                        {isRecategorizing ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Processing...
                            </>
                        ) : (
                            'Confirm Recategorization'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Recategorization Results Modal */}
            <Modal
                show={recategorizeResults !== null}
                onHide={() => setRecategorizeResults(null)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {recategorizeResults?.error ? '❌ Recategorization Failed' : '✅ Recategorization Results'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {recategorizeResults?.error ? (
                        <Alert variant="danger">
                            <strong>Error:</strong> {recategorizeResults.error}
                        </Alert>
                    ) : (
                        <div>
                            <Alert variant="success">
                                <strong>Success!</strong> {recategorizeResults?.message}
                            </Alert>

                            <h6>Summary:</h6>
                            <ul>
                                <li>Total processed: {recategorizeResults?.summary?.total_processed}</li>
                                <li>Successful: {recategorizeResults?.summary?.successful}</li>
                                <li>Failed: {recategorizeResults?.summary?.failed}</li>
                            </ul>

                            {recategorizeResults?.results && (
                                <>
                                    <h6>Detailed Results:</h6>
                                    <div className="table-responsive">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Product ID</th>
                                                    <th>Status</th>
                                                    <th>Message/Error</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recategorizeResults.results.map((result, index) => (
                                                    <tr key={index}>
                                                        <td>{result.product_id}</td>
                                                        <td>
                                                            <Badge bg={result.success ? 'success' : 'danger'}>
                                                                {result.success ? 'Success' : 'Failed'}
                                                            </Badge>
                                                        </td>
                                                        <td>{result.message || result.error}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setRecategorizeResults(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Products;